import { ThemeEvents } from '@theme/events';

/**
 * Keeps the GBU Sold Out Sorter app block in sync when the shopper switches variants.
 *
 * The app block decides its own visibility server-side in Liquid: a sold-out variant renders
 * the "Notify me when available" button, an in-stock one renders an empty block. Horizon only
 * morphs <variant-picker> on a same-product variant change, so that block is never updated and
 * the shopper has to reload the page. Two things go wrong without this bridge:
 *
 *   in-stock -> sold-out : the notify button never appears.
 *   sold-out -> in-stock : the notify button stays AND add-to-cart stays hidden, so the
 *                          shopper cannot buy the variant they just selected.
 *
 * The app is a Shopify app extension, so its source can't be changed from the theme. This
 * listens for Horizon's variantUpdate — which already carries the freshly rendered document —
 * and reconciles the block from it.
 *
 * The delicate part is the app's own MutationObserver. On every childList change in the
 * product scope it calls its private hideNative(), which hides add-to-cart and never un-hides.
 * It also resolves its scope by walking up from the notify element, so if that element is ever
 * detached the walk falls through to `document` and it hides every add-to-cart button on the
 * page. Hence two rules below: never detach the notify element (hide it instead), and re-assert
 * the button state on a rAF, which lands after the app's observer microtask.
 */

const WRAPPER = 'div.shopify-app-block[id*="gbu_soldout_sorter"]';
const NOTIFY = '[data-oos-notify]';
const HIDDEN_BY_APP = 'data-oos-hidden';

/**
 * The nearest ancestor holding an add-to-cart form. Mirrors the app's own scope resolution so
 * we act on exactly the buttons it acts on — the main product's, not the recommendation cards
 * further down the same section.
 * @param {Element} el
 * @returns {Element | null} The scope, or null if there isn't one. Unlike the app, we never
 *   fall back to `document`; touching every button on the page is never the right answer.
 */
function productScope(el) {
  let node = el.parentElement;

  while (node && node !== document.body) {
    if (node.querySelector?.('form[action*="/cart/add"]')) return node;
    node = node.parentElement;
  }

  return null;
}

/**
 * The buttons the app considers "native" checkout controls.
 * @param {Element} scope
 * @returns {HTMLElement[]}
 */
function nativeButtons(scope) {
  const selector = [
    'form[action*="/cart/add"] button[name="add"]',
    'form[action*="/cart/add"] button[type="submit"]',
    'form[action*="/cart/add"] input[type="submit"]',
    'form[action*="/cart/add"] .product-form__submit',
    '.shopify-payment-button',
    '[data-shopify="payment-button"]',
  ].join(',');

  return /** @type {HTMLElement[]} */ ([...scope.querySelectorAll(selector)]);
}

/**
 * @param {Element} scope
 */
function hideNative(scope) {
  for (const button of nativeButtons(scope)) {
    button.style.setProperty('display', 'none', 'important');
    button.setAttribute(HIDDEN_BY_APP, '1');
  }
}

/**
 * Undoes the app's hiding. Only touches buttons it actually marked, and only via attributes —
 * the app watches childList, so this cannot bounce back into its observer.
 * @param {Element} scope
 */
function showNative(scope) {
  for (const button of scope.querySelectorAll(`[${HIDDEN_BY_APP}]`)) {
    if (!(button instanceof HTMLElement)) continue;
    button.style.removeProperty('display');
    button.removeAttribute(HIDDEN_BY_APP);
  }
}

class SoldOutSorterBridge {
  /** @type {Element} */
  #wrapper;
  /** @type {Element | null} */
  #section;
  /** Whether the currently selected variant is sold out. */
  #soldOut = false;
  /** @type {number} */
  #frame = 0;
  /** @type {MutationObserver | undefined} */
  #observer;

  /** @param {Element} wrapper */
  constructor(wrapper) {
    this.#wrapper = wrapper;
    this.#section = wrapper.closest('.shopify-section');
    this.#soldOut = Boolean(wrapper.querySelector(NOTIFY));

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    const html = detail?.data?.html;
    if (!(html instanceof Document)) return;

    // Product cards and quick-add dialogs fire this too. Only react to our own section.
    const target = event.target;
    if (target instanceof Element && target.closest('.shopify-section') !== this.#section) return;

    const fresh = html.getElementById(this.#wrapper.id);
    // If the app block is absent from the response, the app is mid-deploy or disabled. Leaving
    // the DOM untouched is the safe outcome; re-asserting nothing is better than guessing.
    if (!fresh) return;

    this.#soldOut = Boolean(fresh.querySelector(NOTIFY));
    this.#reconcile(fresh);
  };

  /**
   * @param {Element} fresh - The freshly rendered app block for the selected variant.
   */
  #reconcile(fresh) {
    const existing = this.#wrapper.querySelector(NOTIFY);
    const freshNotify = fresh.querySelector(NOTIFY);

    if (this.#soldOut && freshNotify) {
      if (existing) {
        // Reuse the initialised node — replacing it would detach it from the app's observer.
        // Copying the data-* across keeps the subscribe payload right if the product changed.
        for (const { name, value } of [...freshNotify.attributes]) {
          if (name.startsWith('data-')) existing.setAttribute(name, value);
        }
        /** @type {HTMLElement} */ (existing).style.removeProperty('display');
      } else {
        // Nothing to preserve: the block rendered empty, so this is a plain insert.
        this.#wrapper.innerHTML = fresh.innerHTML;
        this.#bootApp();
      }
    } else if (existing) {
      /** @type {HTMLElement} */ (existing).style.setProperty('display', 'none', 'important');
    }

    this.#watchScope();
    this.#reassert();
  }

  /**
   * Tells the app extension to initialise the node we just inserted. Its script listens for
   * shopify:section:load for exactly this purpose, and its own `__oosInit` guard means already
   * initialised blocks are skipped. Nothing else on a product page listens for this event —
   * the theme's other listeners live in design-mode-only or non-product scripts.
   */
  #bootApp() {
    const sectionId = this.#section?.id?.replace(/^shopify-section-/, '') ?? '';

    (this.#section ?? document).dispatchEvent(
      new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId } })
    );
  }

  /**
   * The app's observer re-hides add-to-cart on any childList change and never restores it, so
   * anything that re-renders the buy buttons later would strand an in-stock variant with no way
   * to buy. Re-assert on the same mutations it reacts to.
   */
  #watchScope() {
    if (this.#observer) return;

    const scope = this.#currentScope();
    if (!scope) return;

    this.#observer = new MutationObserver(() => this.#reassert());
    this.#observer.observe(scope, { childList: true, subtree: true });
  }

  /**
   * Applies the desired button state on the next frame. rAF runs after the microtask queue, so
   * this always lands after the app's observer callback rather than racing it.
   */
  #reassert() {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;

      const scope = this.#currentScope();
      if (!scope) return;

      const notify = this.#wrapper.querySelector(NOTIFY);
      const wantsHide = this.#soldOut && notify?.getAttribute('data-hide-native') === 'true';

      if (wantsHide) hideNative(scope);
      else showNative(scope);
    });
  }

  /** @returns {Element | null} */
  #currentScope() {
    return productScope(this.#wrapper.querySelector(NOTIFY) ?? this.#wrapper);
  }
}

for (const wrapper of document.querySelectorAll(WRAPPER)) {
  new SoldOutSorterBridge(wrapper);
}
